import React, { useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import apiClient from '../api/apiClient';
import './PaymentOptionsModal.css';
import '../css/mainhome.css';

import googlePayIcon from '../assets/icons/google-pay-or-tez-logo-svgrepo-com.svg';
import phonePeIcon from '../assets/icons/phonepe-icon.svg';
import paytmIcon from '../assets/icons/paytm_icon-icons.com_62778.svg';

// ---- Helpers ----
const buildUpiQS = ({ pa, pn, am, tr, tn }) =>
  new URLSearchParams({
    pa: pa || '',
    pn: pn || '',
    am: (Number(am) || 0).toFixed(2),
    tr: tr || '',
    tn: tn || '',
    cu: 'INR',
  }).toString();

const FALLBACK_UPI_ID = 'tatatarvals786@finobank';

const pickString = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const isValidUpiId = (value) =>
  /^[A-Za-z0-9._-]{2,256}@[A-Za-z0-9._-]{2,64}$/.test(String(value || '').trim());

const readFeeSettings = () => {
  try {
    return JSON.parse(localStorage.getItem('feeSettings') || '{}') || {};
  } catch {
    return {};
  }
};

const getSafeUpiId = (info) => {
  const fromApi = pickString(info, ['upiID', 'upiId', 'UPIID', 'upi_id', 'merchantUpi', 'merchantUPI']);
  if (isValidUpiId(fromApi)) return fromApi;

  const settings = readFeeSettings();
  const fromSettings = pickString(settings, [
    'upiId',
    'upiID',
    'UPIID',
    'upi_id',
    'merchantUpi',
    'merchantUPI',
    'depositUpi',
    'staticUpi',
    'staticUPI',
    'payUpiId',
    'deposit_upi',
  ]);
  if (isValidUpiId(fromSettings)) return fromSettings;

  return FALLBACK_UPI_ID;
};

const buildUpiIntentUrl = ({ depositType, qs }) => {
  return `upi://pay?${qs}`;
};

const PaymentOptionsModal = ({ amount, onClose }) => {
  const { user } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);

  const handlePayment = async (depositType) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const registerId = user?.registerId || localStorage.getItem('registerId');
      if (!localStorage.getItem('accessToken') || !registerId) throw new Error('Authentication error. Please log in again.');

      // 1) Create payment session
      const { data } = await apiClient.post('/deposit-create-upi-fund-request', {
        registerId,
        depositType,
        amount: Number(amount),
        hashKey: 'chela',
      });

      if (!data?.status || !data?.info) {
        throw new Error(data?.msg || 'Failed to create payment request.');
      }

      const upiID = getSafeUpiId(data.info);
      const paymentHash = pickString(data.info, ['paymentHash', 'payment_hash', 'hash', 'txnId', 'transactionId']);
      const remark = pickString(data.info, ['remark', 'remarks', 'note', 'tn']);
      const timestamp = data.info.timestamp;
      const merchantName = 'SARA777';

      if (!upiID) {
        throw new Error(data?.msg || 'Payment UPI ID missing. Please try again later.');
      }

      // Persist for verify step (DO NOT clear now). Stamp createdAt.
      localStorage.setItem('pendingTransaction', JSON.stringify({
        registerId,
        depositType,
        amount: Number(amount),
        hashKey: 'chela',
        timestamp,
        paymentHash,
        remark,
        upiID,
        createdAt: Date.now()
      }));
      sessionStorage.setItem('upiPaymentAwaitingConfirmation', JSON.stringify({
        remark,
        createdAt: Date.now()
      }));

      // 2) Open UPI
      const qs = buildUpiQS({
        pa: upiID,
        pn: merchantName,
        am: amount,
        tr: paymentHash,
        tn: String(remark ?? ''),
      });

      const upiUrl = `upi://pay?${qs}`;
      const launchUrl = buildUpiIntentUrl({ depositType, qs });
      window.location.href = launchUrl || upiUrl;
      window.setTimeout(() => {
        if (typeof onClose === 'function') onClose();
      }, 600);
    } catch (err) {
      console.error('Payment initiation failed:', err);
      alert(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="payment-modal-overlay"
      onClick={!isLoading ? onClose : undefined}
      role="dialog"
      aria-modal="true"
    >
      <div className="payment-modal-content" onClick={(e) => e.stopPropagation()}>
        <h4>Select Payment Method</h4>

        {isLoading ? (
          <div className="payment-loader">Loading...</div>
        ) : (
          <div className="payment-options">
            <button
              className="payment-option-btn"
              type="button"
              disabled={isLoading}
              onClick={() => handlePayment('googlePay')}
            >
              <img src={googlePayIcon} alt="Google Pay" />
              <span>Google Pay</span>
            </button>

            <button
              className="payment-option-btn"
              type="button"
              disabled={isLoading}
              onClick={() => handlePayment('phonePe')}
            >
              <img src={phonePeIcon} alt="PhonePe" />
              <span>PhonePe</span>
            </button>

            <button
              className="payment-option-btn"
              type="button"
              disabled={isLoading}
              onClick={() => handlePayment('paytm')}
            >
              <img src={paytmIcon} alt="Paytm" />
              <span>Paytm</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentOptionsModal;
